import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import { HttpClient } from '@angular/common/http';
import { map, Observable, BehaviorSubject, tap, shareReplay } from 'rxjs';
import { Category } from '../model/category';

@Injectable({
  providedIn: 'root',
})
export class GraphqlService {
  private apiUrl = `${environment.api}/graphql`;

  // Add BehaviorSubject for categories
  private categoriesSubject = new BehaviorSubject<Category[]>([]);
  public categories$ = this.categoriesSubject.asObservable();

  constructor(private http: HttpClient) {
    // Initialize categories on service creation
    this.loadCategories().subscribe();
  }

  // Private method to load categories from API
  private loadCategories(): Observable<Category[]> {
    const query = `
      query {
        allCategories {
          id
          name
          description
          parentId
        }
      }
    `;

    return this.http
      .post<{
        data: {
          allCategories: Category[];
        };
      }>(this.apiUrl, { query })
      .pipe(
        map((response) => {
          response.data.allCategories.sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          return response.data.allCategories;
        }),
        tap((categories) => this.categoriesSubject.next(categories))
      );
  }

  // Updated public method that uses cache
  private categoriesCache$: Observable<Category[]> | null = null;

  allCategories(): Observable<Category[]> {
    if (!this.categoriesCache$) {
      console.log('loading categories from api');
      this.categoriesCache$ = this.loadCategories().pipe(shareReplay(1));
    }
    console.log('returning cached categories');
    return this.categoriesCache$;
  }

  // Method to force refresh categories
  refreshCategories(): Observable<Category[]> {
    return this.loadCategories();
  }

  // Method to get current categories synchronously
  getCurrentCategories(): Category[] {
    return this.categoriesSubject.getValue();
  }

  // Method to clear cached categories
  clearCachedCategories(): void {
    this.categoriesSubject.next([]);
  }

  categoriesByParentId(parentId: string | null) {
    const query = `
      query {
        categoriesByParentId(parentId: ${parentId ? `"${parentId}"` : null}) {
          id
          name
          parentId
          description
        }
      }
    `;
    return this.http.post(this.apiUrl, { query });
  }

  deleteCategory(
    id: string
  ): Observable<{ data: { deleteCategory: boolean } }> {
    const query = `
      mutation {
        deleteCategory(id: "${id}")
      }
    `;

    this.categoriesCache$ = null;

    return this.http.post<{ data: { deleteCategory: boolean } }>(this.apiUrl, {
      query,
    });
  }

  categoriesWithPathByParentId(parentId: string | null) {
    const query = `
      query {
        categoriesWithPathByParentId(parentId: ${
          parentId ? `"${parentId}"` : null
        }) {
          path {
            id
            name
          }
          categories {
            id
            name
            description
          }
          currentCategory {
            id
            name
          }
        }
      }
    `;
    return this.http.post(this.apiUrl, { query });
  }

  addCategory(
    name: string,
    description: string,
    parentId: string | null | undefined
  ) {
    const mutation = `
    mutation {
      addCategory(name: "${name}", description: "${description}", parentId: ${
      parentId ? `"${parentId}"` : null
    }) {
        name
        description
        parentId
      }
    }
  `;

    this.categoriesCache$ = null;
    return this.http.post(this.apiUrl, { query: mutation });
  }

  updateCategory(
    id: string,
    name: string,
    description: string,
    parentId: string | null | undefined
  ) {
    const mutation = `
      mutation {
        updateCategory(id: "${id}", name: "${name}", description: "${description}", parentId: ${
      parentId ? `"${parentId}"` : null
    }) {
          id
          name
          description
          parentId
        }
      }
    `;

    this.categoriesCache$ = null;

    return this.http.post(this.apiUrl, { query: mutation });
  }
}
